import React, { useState, useEffect } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import WarningIcon from "@material-ui/icons/Warning";
import {
	updateFieldValues,
	appNotify
} from 'store/actions';
import { parsePastedData } from 'sagas/importRawDataSaga';
import { format, addMinutes, getYear, addDays } from 'date-fns';

function cleanData(DragAndDropData, eligibleHeaders, selectedGroup) {
	try {
		let cleanedRows = [];
		
		DragAndDropData.forEach((row, index) => {
			const rowWithCleanedKeys = {};
			Object.keys(row).forEach(key => { // Remap row object to cleaned keys
				const cleanedKey = key.replace(/\s+/g, '').toLowerCase();
				rowWithCleanedKeys[cleanedKey] = row[key];
			});

			const id = 'shapename';
			if (!rowWithCleanedKeys[id]) { // Don't add row if it doesn't include id
				return;
			}

			// For incoming excel data, null cells simply don't exist in the row object, so we need to loop through the eligibleHeaders list and restore null keys
			const importRow = {};
			eligibleHeaders.forEach(header => {
				const mappedKey = header.field; // Use eligibleHeaders to map cleaned key name to final key name
				let foundValue = null;
				if (rowWithCleanedKeys.hasOwnProperty(header.fieldToLowerCase)) { // Try to find the value in the row data and clean it
					let trimmedCell = rowWithCleanedKeys[header.fieldToLowerCase];
					if (header.type === 'number') {
						trimmedCell = trimmedCell.toString().trim();
						if (trimmedCell !== '') { // Don't operate on empty string or it will be converted to a 0
							const cleanTest = Number(trimmedCell.replace(/[\s$,]+/g, ''));
							if (!isNaN(cleanTest)) { // If removing $ and commas from cell string results in parsable number, use cleaned value. Else, leave original string so that it fails validation later
								trimmedCell = cleanTest.toString();
							}
						}
					} else if (header.type === 'date') {
						if (trimmedCell instanceof Date) {
							if (getYear(trimmedCell) === 1899) { // Excel does not support dates before 1900, causes parser to have rare case where date of 1/1/1900 or 1/0/1900 gets parsed to the year 1899 and one day off. Adding one day to these dates shifts it back to the correct day
								trimmedCell = addDays(trimmedCell, 1);
							}

							let diff = 0; // When the parser is creating JS date objects from the cells, it will automatically assign daylight savings timezone based on the date. This can cause winter dates (not in daylight savings) to be shifted back an hour to 11pm of the previous day.
							if (trimmedCell.getTimezoneOffset() !== new Date().getTimezoneOffset()) { // To fix this, we detect if date is not in same timezone as local machine (due to the daylight savings shifting)...
								diff = trimmedCell.getTimezoneOffset() - new Date().getTimezoneOffset(); // ...get the difference in minutes...
							}
							// console.log(trimmedCell, trimmedCell.toLocaleDateString(), trimmedCell.getTimezoneOffset())
							trimmedCell = format(addMinutes(trimmedCell, diff), 'MM/dd/yyyy'); // ...and add it to the parsed date to shift it back to our local timezone.
						} else {
							trimmedCell = trimmedCell.toString().trim();
						}
					} else if (header.type === 'string') {
						trimmedCell = trimmedCell.toString().trim();
					} else if (header.type === 'boolean') {
						trimmedCell = trimmedCell.toString().trim();
						// If the input was a string (accept yes/no, true/false), convert to bool true/false respectively
						const toLower = trimmedCell.toLowerCase();
						if (toLower === 'yes' || toLower === 'true' || toLower === 'y' || toLower === '1') {
							trimmedCell = true;
						} else if (toLower === 'no' || toLower === 'false' || toLower === 'n' || toLower === '0') {
							trimmedCell = false;
							// } else {
							//   trimmedCell = null; // other values default to null to trigger validation error 
						}
					}
					if (trimmedCell === '') {
						trimmedCell = null;
					}
					foundValue = trimmedCell;
				}
				importRow[mappedKey] = foundValue; // Add final key name and either cleaned value or null to row
			})
			cleanedRows.push(importRow);
		});

		let hasTypeError = false;
		cleanedRows.forEach(row => {
			Object.keys(row).forEach(key => {
				if (key === 'Shape name') return;
				const field = selectedGroup.fields.find(field => field.name.toLowerCase() === key.toLowerCase());
				const value = row[key];
				if (field.assumptionFieldDataType === 'Number') {
					if (!Number.isFinite(Number(value))) {
						hasTypeError = true;
					}
				} else if (field.assumptionFieldDataType === 'String') {
					if (typeof value !== 'string') {
						hasTypeError = true;
					}
				} else {
					console.log(`unsupported type found for assumption field ${field}`);
				}
			})
		})

		if (hasTypeError) {
			appNotify('Please correct errors in data', 'warning');
		}

		return { cleanedRows, hasTypeError };
	}
	catch (error) {
		appNotify(error.message, 'error');
		console.log('cleanData', error.message)
	}
}

const ConfirmationModal = ({ classes, open, onClose, selectedGroup, updateFieldValues, appNotify }) => {
	const [foundRowCount, setFoundRowCount] = useState(null);
	const [foundFieldCount, setFoundFieldCount] = useState(null);
	const [updatedShapes, setUpdatedShapes] = useState(null);
	const [isSaving, setIsSaving] = useState(false);

  const handleClose = (e, reason) => {
    if (reason === 'backdropClick') return; // updated handling for disableBackdropClick
		setFoundRowCount(null);
		setFoundFieldCount(null);
		setUpdatedShapes(null);
    onClose();
  }

  const handlePaste = async e => {
		setFoundRowCount(null);
		setFoundFieldCount(null);
		setUpdatedShapes(null);
    try {
      const data = await navigator.clipboard.readText();

			const eligibleHeaders = [{ field: 'Shape name', fieldToLowerCase: 'shapename' }, ...selectedGroup.fields.map(field => {
				return {
					field: field.name,
					fieldToLowerCase: field.name.replace(/\s+/g, '').toLowerCase()
				}
			})];
			const newImportRows = [];
			const rows = data.split('\r\n');
			const cleanedRows = rows.filter(r => r !== '');  // Remove empty rows, this is usually the last row
			if (cleanedRows.length) {
				const headerRow = cleanedRows.shift().split('\t').map(header => header.trim()); // Shift out first row to check for headers
				const foundAtLeastOne = headerRow.some(c => eligibleHeaders.find(el => el.fieldToLowerCase === c.replace(/\s+/g, '').toLowerCase()));
	
				if (foundAtLeastOne) {
					if (cleanedRows.length) { // Split remaining data rows into cells
						cleanedRows.forEach(row => {
							const cells = row.split('\t');
							const newRow = {};
							headerRow.forEach((col, index) => {
								newRow[col] = cells[index];
							})
							newImportRows.push(newRow);
						});
					} else {
						console.log('No data rows were found');
						appNotify('No data rows were found', 'warning');
						return;
					}
				} else {
					console.log('No matching headers were found');
					appNotify('No matching headers were found', 'warning');
					return;
				}
			}
			const cleanedDataResult = cleanData(newImportRows, eligibleHeaders, selectedGroup);
			const updatedShapes = [];
			if (!cleanedDataResult.hasTypeError) {
				let foundRows = 0;
				let foundFields = 0;
				cleanedDataResult.cleanedRows.forEach((row, rowIndex) => {
					var shape = selectedGroup.shapes.find((s) => s.shapeName === row['Shape name']);
					if (shape) {
						foundRows++;
						let updatedShape = { ...shape };
						let updatedFieldValues = [...shape.fieldValues];

						for (const [key, value] of Object.entries(row)) {
							const fieldId = selectedGroup.fields.find(el => el.name === key)?.fieldId;
							if (fieldId == null) continue;

							var index = updatedFieldValues.findIndex(
								(fv) => fv.fieldId === fieldId
							);

							if (index >= 0) {
								if (value != null) {
									updatedFieldValues[index] = {
										...updatedFieldValues[index],
										value: value,
									};
									if (rowIndex === 0) {
										foundFields++;
									}
								}
							}
						}

						updatedShape.fieldValues = updatedFieldValues;
						updatedShapes.push(updatedShape);
					}
				});
				if (foundRows === 0) {
					appNotify('No data rows were found', 'warning');
				} else if (foundFields > 0) {
					setFoundRowCount(foundRows);
					setFoundFieldCount(foundFields);
					setUpdatedShapes(updatedShapes);
					appNotify('Pasted from clipboard', 'success');
				} else {
					appNotify('No data fields were found', 'warning');
				}
			} else {
				appNotify('Incorrect data type found in pasted data', 'warning');
			}
    } catch (err) {
      appNotify(`Paste Error: ${JSON.stringify(err)}`, 'error');
    }
    // } else {
    //   console.log(result.state)
    //   appNotify(`Unable to read from clipboard`, 'error');
    // }
  };

	const handleSave = () => {
		setIsSaving(true);
		const callback = () => {
			setIsSaving(false);
			handleClose();
		}
		updateFieldValues(updatedShapes, callback);
	}
  
  return (
    <Dialog onClose={handleClose} open={open} className={classes.dialog}>
      <DialogTitle>
        Bulk Edit
      </DialogTitle>
      <IconButton className={classes.closeButton} onClick={handleClose}>
        <ClearIcon fontSize="inherit" />
      </IconButton>
      <DialogContent>
        <DialogContentText id="alert-dialog-description" style={{ whiteSpace: 'pre-line' }}>
          Paste in copied data to update values for shapes.
        </DialogContentText>
				{foundRowCount != null && foundFieldCount != null &&
					<div style={{ marginBottom: 12 }}>
						<div>
							Rows to be updated: {foundRowCount} of {selectedGroup.shapes.length}
						</div>
						<div>
							Fields to be updated: {foundFieldCount} of {selectedGroup.fields.length}
						</div>
					</div>
				}
        <Button onClick={handlePaste} variant="contained" color="primary">Paste</Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={!updatedShapes}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

const styles = theme => ({
  dialog: {
    minWidth: '600px',
    minHeight: '450px'
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    // backgroundColor: '#ffffff'
  },
  warningIcon: {
    fill: theme.palette.warning.text,
    verticalAlign: 'middle',
    marginRight: 4
  },
});

const mapState = ({ assumptionGroupsState }) => ({
  selectedGroup: assumptionGroupsState.selectedGroup
});

const mapDispatch = {
	updateFieldValues,
	appNotify
}

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(ConfirmationModal);